import { EReduxQueryTags } from 'shared/types/api.interface';
import {
  IDebateData,
  ICancelDebateRequest,
  IEndDebateRequest,
  EDebateStatuses,
} from 'shared/types/debates.interface';
import { handleSnackbarNotify } from 'utils/handleSnackbarNotify';
import { handleCatchNotValidationError } from 'utils/handleCatchNotValidationError';
import { DEBATE_ENDING_OR_LEAVING } from 'shared/constants/alert-constants';
import { queryUrls } from 'shared/constants/queryApiUrl-constants';
import { LOCAL_STORAGE } from 'shared/constants/local-storage-constants';
import { splitApi } from './split.api';

export const DebateApi = splitApi.injectEndpoints({
  endpoints: (build) => ({
    getAllDebates: build.query<IDebateData[], void>({
      query: () => ({
        url: queryUrls.debateApi.getAllDebates,
      }),
      providesTags: [EReduxQueryTags.Debate],
      keepUnusedDataFor: 35,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleCatchNotValidationError(err, dispatch);
        }
      },
    }),
    getMyDebates: build.query<IDebateData[], void>({
      query: () => ({
        url: queryUrls.debateApi.getMyDebates,
      }),
      providesTags: [EReduxQueryTags.Debate],
      keepUnusedDataFor: 35,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleCatchNotValidationError(err, dispatch);
        }
      },
    }),
    cancelDebate: build.mutation<IDebateData, ICancelDebateRequest>({
      query: (params) => ({
        url: `${queryUrls.debateApi.getAllDebates}${params.id}${queryUrls.debateApi.cancelDebate}`,
        method: 'PATCH',
      }),
      invalidatesTags: () => [EReduxQueryTags.Challenge, EReduxQueryTags.Debate],
      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          handleSnackbarNotify(dispatch, params.message, 'success');
        } catch (err) {
          handleCatchNotValidationError(err, dispatch);
        }
      },
    }),
    checkDebateUser: build.mutation<any, number | string>({
      query: (id) => ({
        url: `${queryUrls.debateApi.getAllDebates}${id}${queryUrls.debateApi.checkDebateUser}`,
        method: 'PATCH',
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleCatchNotValidationError(err, dispatch);
        }
      },
    }),
    endDebate: build.mutation<any, IEndDebateRequest>({
      query: (params) => ({
        url: `${queryUrls.debateApi.getAllDebates}${params.id}${queryUrls.debateApi.endDebate}`,
        method: 'PATCH',
      }),
      invalidatesTags: (result) => {
        if (result) {
          return [EReduxQueryTags.Challenge, EReduxQueryTags.Debate, EReduxQueryTags.CurrentDebate];
        }
        return [];
      },
      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          handleSnackbarNotify(dispatch, params.message, 'info');
          localStorage.removeItem(LOCAL_STORAGE.currentDebateId);
        } catch (err) {
          if (err.error.status === 403) {
            handleSnackbarNotify(
              dispatch,
              params.status === EDebateStatuses.InProgress
                ? DEBATE_ENDING_OR_LEAVING.youLeft
                : params.message,
              'info',
            );
          }
          handleCatchNotValidationError(err, dispatch);
        }
      },
    }),
    getCurrentDebate: build.query<IDebateData, string | number>({
      query: (id) => ({
        url: `${queryUrls.debateApi.getAllDebates}${id}/`,
      }),
      providesTags: [EReduxQueryTags.CurrentDebate],
      keepUnusedDataFor: 35,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleCatchNotValidationError(err, dispatch);
        }
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetMyDebatesQuery,
  useGetAllDebatesQuery,
  useCancelDebateMutation,
  useCheckDebateUserMutation,
  useEndDebateMutation,
  useGetCurrentDebateQuery,
} = DebateApi;
